import React, { useEffect, useState } from "react";
import { FormControl, InputGroup } from "react-bootstrap";
import {
  IoEyeOffOutline,
  IoEyeOutline,
  IoLockClosedOutline,
} from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiClient from "../../api/apiClient";
import { getAuthLinkParams } from "../../modules/helpers";
import { handleErrors } from "../../modules/handleErrors";
import PasswordRequirements from "../../components/PasswordRequirements";
import { getPasswordError } from "../../utils/passwordValidation";
import AuthPageLayout, {
  AuthLoadingState,
  AuthPrimaryButton,
  AuthSecondaryLink,
  AuthStatusIcon,
} from "./AuthPageLayout";

const STATES = {
  LOADING: "loading",
  INVALID: "invalid",
  ERROR: "error",
  FORM: "form",
};

const PasswordField = ({
  id,
  label,
  value,
  onChange,
  showPassword,
  onToggle,
  placeholder,
}) => (
  <div className="form-group mb-3 text-start">
    <label
      htmlFor={id}
      className="form-label mb-1"
      style={{ fontSize: "13px", color: "#4a4a4a" }}
    >
      {label}
    </label>
    <InputGroup className="position-relative">
      <InputGroup.Text
        className="position-absolute bg-white border-0 px-2"
        style={{
          zIndex: 10,
          left: 10,
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        <IoLockClosedOutline style={{ color: "#868B96" }} />
      </InputGroup.Text>
      <FormControl
        required
        id={id}
        type={showPassword ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="new-password"
        minLength={8}
        className="form-control rounded-5 border-0 shadow-sm ps-5 py-2"
        style={{
          height: "50px",
          fontSize: "13px",
          paddingRight: "44px",
        }}
      />
      <button
        type="button"
        className="position-absolute border-0 bg-transparent d-flex align-items-center justify-content-center"
        style={{
          zIndex: 10,
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          padding: 0,
          color: "#868B96",
          cursor: "pointer",
        }}
        onClick={onToggle}
        aria-label={showPassword ? "Hide password" : "Show password"}
        tabIndex={0}
      >
        {showPassword ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
      </button>
    </InputGroup>
  </div>
);

const ConfirmPassword = () => {
  const navigate = useNavigate();
  const { uid, token } = getAuthLinkParams();

  const [pageState, setPageState] = useState(STATES.LOADING);
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loader, setLoader] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const verifyLink = async () => {
      if (!uid || !token) {
        setPageState(STATES.INVALID);
        setErrorMessage("This password reset link is invalid or incomplete.");
        return;
      }

      try {
        const response = await apiClient.post("/verify-reset-password/", {
          uid,
          token,
        });

        if (!response.ok || response.data?.valid === false) {
          setPageState(STATES.ERROR);
          setErrorMessage(
            response.data?.error ||
              "Password reset link is invalid or has expired."
          );
          return;
        }

        if (response.data?.email) setEmail(response.data.email);
        setPageState(STATES.FORM);
      } catch {
        setPageState(STATES.ERROR);
        setErrorMessage("Something went wrong. Please try again later.");
      }
    };

    verifyLink();
  }, [uid, token]);

  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, (_, start, middle, domain) => {
        const hidden = middle.length > 0 ? "••••" : "";
        return `${start}${hidden}${domain}`;
      })
    : null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    const passwordError = getPasswordError(password);
    if (passwordError) {
      setFormError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }

    try {
      setLoader(true);
      const response = await apiClient.post("/confirm-password/", {
        uid,
        token,
        new_password: password,
      });
      setLoader(false);

      if (!response.ok) {
        if (response.data?.new_password) {
          handleErrors(response.data);
          return;
        }
        setFormError(
          response.data?.error ||
            "Password reset link is invalid or has expired."
        );
        return;
      }

      toast.success(response.data?.message || "Password has been reset.");
      navigate("/login");
    } catch {
      setLoader(false);
      toast.error("Something went wrong. Please try again later.");
    }
  };

  if (pageState === STATES.LOADING) {
    return (
      <AuthPageLayout
        title="Reset your password"
        subtitle="We're verifying your reset link. This only takes a moment."
      >
        <AuthLoadingState message="Verifying your reset link..." />
      </AuthPageLayout>
    );
  }

  if (pageState === STATES.INVALID || pageState === STATES.ERROR) {
    return (
      <AuthPageLayout>
        <AuthStatusIcon type="error" />
        <h5 className="mb-2">Reset link expired</h5>
        <p className="mb-4" style={{ fontSize: "14px", color: "#6c6c6c" }}>
          {errorMessage}
        </p>
        <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center align-items-center">
          <AuthPrimaryButton onClick={() => navigate("/forgot-password")}>
            Request new link
          </AuthPrimaryButton>
          <AuthSecondaryLink to="/login">Back to login</AuthSecondaryLink>
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      title="Create a new password"
      subtitle={
        maskedEmail
          ? `Choose a strong password for ${maskedEmail}.`
          : "Choose a strong password for your account."
      }
    >
      <form onSubmit={onSubmit}>
        <PasswordField
          id="new-password"
          label="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          showPassword={showPassword}
          onToggle={() => setShowPassword((prev) => !prev)}
          placeholder="At least 8 characters"
        />
        <PasswordRequirements password={password} touched={password.length > 0} />

        <PasswordField
          id="confirm-password"
          label="Confirm password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          showPassword={showConfirmPassword}
          onToggle={() => setShowConfirmPassword((prev) => !prev)}
          placeholder="Re-enter your password"
        />

        {formError && (
          <p
            className="text-start mb-3"
            style={{ fontSize: "13px", color: "#C62828" }}
            role="alert"
          >
            {formError}
          </p>
        )}

        <div className="d-flex justify-content-center mt-4">
          <AuthPrimaryButton type="submit" disabled={loader}>
            {loader ? "Updating..." : "Update password"}
          </AuthPrimaryButton>
        </div>
      </form>
    </AuthPageLayout>
  );
};

export default ConfirmPassword;
